import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateExpense, useExpenseCategories } from '@/api/expenses'
import type { ExpenseBody } from '@/api/expenses'
import { useIdempotencyKey } from '@/api/idempotency'
import { useWorkspace } from '@/api/workspace'
import { todayIn } from '@/components/expenses/dates'
import { expenseErrorMessage, needsBackdateReason } from '@/components/expenses/error-message'
import { ExpenseForm } from '@/components/expenses/expense-form'
import type { ExpenseFormValues } from '@/components/expenses/expense-form'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { Card, CardContent } from '@/components/ui/card'

// Record an Expense (UI-SCREENS E-02, 07a #194). Owner and Staff both may; the Expense moves no
// stock, so nothing in inventory or COGS changes.
export const Route = createFileRoute('/w/$slug/expenses/new')({
  component: NewExpense,
  staticData: { breadcrumb: 'expenses.form.title' },
})

function NewExpense() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const navigate = useNavigate()
  const workspace = useWorkspace(slug)
  const categories = useExpenseCategories(slug)
  const create = useCreateExpense()
  const idempotency = useIdempotencyKey()
  // The backdate limit lives on the server; the reason field appears once it asks for one.
  const [askReason, setAskReason] = useState(false)

  // The reserved "Material restock" category belongs to Restocks, and an archived one is out of
  // new entry (G27).
  const selectable = (categories.data ?? []).filter((category) => !category.system && !category.archived_at)

  function onSubmit(values: ExpenseFormValues) {
    const reason = values.backdateReason.trim()
    const body: ExpenseBody = {
      expense: {
        occurred_on: values.occurredOn,
        expense_category_id: Number(values.categoryId),
        amount: Number(values.amount),
        vendor: values.vendor.trim() === '' ? null : values.vendor.trim(),
        note: values.note.trim() === '' ? null : values.note.trim(),
        ...(reason === '' ? {} : { backdate_reason: reason }),
      },
    }
    create.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: () => navigate({ to: '/w/$slug/expenses', params: { slug } }),
        onSettled: (_data, error) => {
          idempotency.settle(error)
          if (needsBackdateReason(error)) setAskReason(true)
        },
      },
    )
  }

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/expenses" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('expenses.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('expenses.form.title')}</h1>

      {workspace.isPending || categories.isPending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : workspace.isError || !workspace.data || categories.isError || !categories.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !workspace.data.adopted_on ? (
        <Missing message={t('expenses.errors.adoptionDateNotSet')} />
      ) : selectable.length === 0 ? (
        <Missing message={t('expenses.form.noCategories')} />
      ) : (
        <ExpenseForm
          slug={slug}
          adoptedOn={workspace.data.adopted_on}
          today={todayIn(workspace.data.timezone)}
          categories={selectable}
          defaults={{
            occurredOn: todayIn(workspace.data.timezone),
            categoryId: '',
            amount: '',
            vendor: '',
            note: '',
            backdateReason: '',
          }}
          pending={create.isPending}
          errorMessage={create.isError ? expenseErrorMessage(t, create.error) : ''}
          askReason={askReason}
          submitKey="expenses.form.save"
          onSubmit={onSubmit}
        />
      )}
    </section>
  )
}

function Missing({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p role="status" className="text-sm">
          {message}
        </p>
      </CardContent>
    </Card>
  )
}
